import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Robot, Article, ShareNetwork, Funnel, ChatCircle, ChartLineUp,
  Play, Pause, CheckCircle, Clock, WarningCircle
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";

const agentConfig = {
  content: { icon: Article, color: "text-amber-400", bg: "bg-amber-500/10", description: "Produces new lessons based on trending topics" },
  social: { icon: ShareNetwork, color: "text-cyan-400", bg: "bg-cyan-500/10", description: "Publishes and schedules social media posts" },
  funnel: { icon: Funnel, color: "text-violet-400", bg: "bg-violet-500/10", description: "Optimizes funnels for better conversions" },
  community: { icon: ChatCircle, color: "text-emerald-400", bg: "bg-emerald-500/10", description: "Assists academy members with questions" },
  analytics: { icon: ChartLineUp, color: "text-rose-400", bg: "bg-rose-500/10", description: "Tracks traffic, engagement, and conversions" },
};

const AgentCard = ({ agent, onToggle, onApprovalToggle, onRun }) => {
  const config = agentConfig[agent.agent_type] || agentConfig.content;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`agent-card p-6 ${agent.is_active ? 'active' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center`}>
            <Icon size={24} className={config.color} weight="duotone" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {agent.name}
            </h3>
            <p className="text-xs text-zinc-500">{config.description}</p>
          </div>
        </div>
        <div className={`w-3 h-3 rounded-full ${agent.is_active ? 'bg-emerald-400 status-dot' : 'bg-zinc-600'}`} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-zinc-900/50 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Tasks Completed</p>
          <p className="text-xl font-bold text-white">{agent.tasks_completed}</p>
        </div>
        <div className="bg-zinc-900/50 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Last Run</p>
          <p className="text-sm text-zinc-300">
            {agent.last_run ? new Date(agent.last_run).toLocaleDateString() : 'Never'}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-4 mb-4">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Active</Label>
          <Switch
            checked={agent.is_active}
            onCheckedChange={(checked) => onToggle(agent.id, checked)}
            data-testid={`agent-${agent.agent_type}-toggle`}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Approval Mode</Label>
          <Switch
            checked={agent.approval_mode}
            onCheckedChange={(checked) => onApprovalToggle(agent.id, checked)}
          />
        </div>
      </div>

      {/* Logs */}
      <div className="mb-4">
        <Label className="text-zinc-500 text-xs uppercase tracking-wider">Recent Logs</Label>
        <ScrollArea className="h-24 mt-2">
          <div className="space-y-1">
            {agent.logs?.slice(-5).reverse().map((log, i) => (
              <div key={i} className="text-xs font-mono text-cyan-400/80 py-1 border-b border-cyan-500/10">
                <span className="text-zinc-600">{new Date(log.timestamp).toLocaleTimeString()}</span>
                {' '}{log.message}
              </div>
            ))}
            {(!agent.logs || agent.logs.length === 0) && (
              <p className="text-xs text-zinc-600">No logs yet</p>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Run Button */}
      <Button
        onClick={() => onRun(agent.id)}
        disabled={!agent.is_active}
        data-testid={`run-agent-${agent.agent_type}`}
        className={`w-full h-10 rounded-xl ${agent.is_active ? 'bg-cyan-500 hover:bg-cyan-400 text-black' : 'bg-zinc-800 text-zinc-500'} font-semibold`}
      >
        <Play size={18} className="mr-2" />
        Run Now
      </Button>
    </motion.div>
  );
};

export default function AIAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`);
      setAgents(res.data);
    } catch (err) {
      toast.error("Failed to fetch agents");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (agentId, isActive) => {
    try {
      await axios.put(`${API}/agents/${agentId}`, { is_active: isActive });
      setAgents(agents.map(a => a.id === agentId ? { ...a, is_active: isActive } : a));
      toast.success(isActive ? "Agent activated" : "Agent deactivated");
    } catch (err) {
      toast.error("Failed to update agent");
    }
  };

  const handleApprovalToggle = async (agentId, approvalMode) => {
    try {
      await axios.put(`${API}/agents/${agentId}`, { approval_mode: approvalMode });
      setAgents(agents.map(a => a.id === agentId ? { ...a, approval_mode: approvalMode } : a));
      toast.success(approvalMode ? "Approval mode enabled" : "Auto-approval enabled");
    } catch (err) {
      toast.error("Failed to update agent");
    }
  };

  const handleRun = async (agentId) => {
    try {
      await axios.post(`${API}/agents/${agentId}/run`);
      fetchAgents(); // Refresh to get updated logs
      toast.success("Agent triggered!");
    } catch (err) {
      toast.error("Failed to run agent");
    }
  };

  const activeCount = agents.filter(a => a.is_active).length;

  return (
    <div data-testid="ai-agents">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI Agents
          </h1>
          <p className="text-zinc-400">Autonomous agents that run your studio 24/7</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl px-4 py-2">
            <span className="text-zinc-400">Active: </span>
            <span className="text-emerald-400 font-bold">{activeCount}/{agents.length}</span>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 mb-8">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} className="text-emerald-400" weight="fill" />
            <span className="text-sm text-zinc-400">Active agents running</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-cyan-400" weight="fill" />
            <span className="text-sm text-zinc-400">Tasks queued for approval</span>
          </div>
          <div className="flex items-center gap-2">
            <WarningCircle size={20} className="text-amber-400" weight="fill" />
            <span className="text-sm text-zinc-400">Requires attention</span>
          </div>
        </div>
      </div>

      {/* Agents Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onToggle={handleToggle}
              onApprovalToggle={handleApprovalToggle}
              onRun={handleRun}
            />
          ))}
        </div>
      )}
    </div>
  );
}
